import { OrderResponse, OrderSource, OrderChannel, DisputeFlowType } from '@afterpaytouch/portal-api'
import { useDisputeTranslation } from '../hooks'
import { useFlag } from '@afterpaytouch/feature'
import { createModalHook, LaunchDarklyFlags } from '../../../hooks'
import { useAmplitudeWithEnduringEventProperties } from '../../../integrations/amplitude'
import { useCallback, useEffect, useMemo } from 'react'
import { isValidObject } from '@afterpay/utils'
import { TrackingEvent } from '../../../model/amplitude'
import { orderTransactionsService } from '@afterpaytouch/portal-api/consumer/ordertransactions'
import { useSection75DisputeModalContext, SelectedItem } from './Section75DisputeModalContext'
import { getDisputeErrorMessage, getDisputeErrorTitle, isAmountOverSection75Threshold, section75ItemQuantity } from '../utils'
import { DisputeReason } from './types'

const S75_EXCLUDED_SOURCES: OrderSource[] = [OrderSource.SINGLE_USE_CARD, OrderSource.CASH_APP_SINGLE_USE_CARD]

const S75_EXCLUDED_CHANNELS: OrderChannel[] = [OrderChannel.IN_STORE, OrderChannel.IN_STORE_CARD, OrderChannel.IN_STORE_CARD_ONLINE]

// Launch is midnight London time; July is BST, hence the +01:00 offset.
const S75_AGREEMENT_START_DATE = Date.parse('2026-07-15T00:00:00+01:00')

export const isOrderEligibleForS75 = (order: OrderResponse): boolean => {
  if (order.orderDate < S75_AGREEMENT_START_DATE) {
    return false
  }
  if (S75_EXCLUDED_SOURCES.includes(order.source)) {
    return false
  }
  if (S75_EXCLUDED_CHANNELS.includes(order.channel)) {
    return false
  }
  return true
}

export interface UseSection75Dispute {
  section75ModalEnabled: boolean
  isModalOpen: boolean
  openModal: () => void
  closeModal: () => void
}

const useModal = createModalHook()

export const getSelectedItemDisputeAmount = (item: SelectedItem): string => {
  if (item.useCustomAmount === true && item.disputeAmount?.amount !== undefined && item.disputeAmount.amount !== '') {
    return item.disputeAmount.amount
  }

  return item.amount?.amount ?? '0'
}

/**
 * Hook to calculate the total disputed amount from selected items.
 * `amount` is already the claim for the whole line (per-unit price x quantity), so this
 * must not multiply by quantity again.
 * @param selectedItems - Array of selected items
 * @returns The total amount as a number
 */
export const useSelectedItemsTotal = (selectedItems: SelectedItem[]): number => {
  return useMemo(() => {
    return selectedItems.reduce((total, item) => {
      return total + Number(getSelectedItemDisputeAmount(item))
    }, 0)
  }, [selectedItems])
}

/**
 * Hook to check if selected items meet Section 75 eligibility (at least one item over 100)
 *
 * Deliberately judged on the per-unit `itemAmount`, never on the line total: two GBP 60
 * units are two GBP 60 items and neither qualifies for Section 75. Do not switch this to
 * `amount`, which is the whole-line claim.
 * @param selectedItems - Array of selected items
 * @returns boolean - True if at least one item meets the threshold
 */
export const useSection75Eligibility = (selectedItems: SelectedItem[]): boolean => {
  return selectedItems.some((item) => {
    return isAmountOverSection75Threshold(item.itemAmount?.amount ?? item.amount.amount ?? '0')
  })
}

export const useSection75Modal = (orderDetail?: OrderResponse, hasActiveDispute = false): UseSection75Dispute => {
  const section75Enabled = useFlag(LaunchDarklyFlags.Section75Enabled)
  const [isModalOpen, { openModal, closeModal }] = useModal()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()

  const section75ModalEnabled: boolean = useMemo(() => {
    if (!isValidObject(orderDetail)) {
      return false
    }
    if (hasActiveDispute) {
      return false
    }
    if (
      orderDetail.disputeFlowType === DisputeFlowType.DECLINE ||
      orderDetail.disputeFlowType === DisputeFlowType.PREZZEE ||
      orderDetail.disputeFlowType === DisputeFlowType.ALIBABA
    ) {
      return false
    }
    if (!isOrderEligibleForS75(orderDetail)) {
      return false
    }
    return section75Enabled
  }, [orderDetail, hasActiveDispute, section75Enabled])

  const openSection75DisputeModal = useCallback(() => {
    if (hasActiveDispute) {
      return
    }
    openModal()
    logEvent(TrackingEvent.PRESSED_INITIAL_REPORTING_PROBLEM)
  }, [hasActiveDispute, openModal, logEvent])

  return {
    section75ModalEnabled,
    isModalOpen,
    openModal: openSection75DisputeModal,
    closeModal,
  }
}

const { useCreateS75DisputeMutation } = orderTransactionsService

type UseCreateSection75Dispute = () => Promise<any>

export const useCreateSection75Dispute = (): UseCreateSection75Dispute => {
  const t = useDisputeTranslation()
  const [createS75Dispute, { isLoading, error, isError, isSuccess }] = useCreateS75DisputeMutation()

  const { setIsLoading, setResult, refreshDisputeInfo, order, reason, files, expectedDeliveryDate, selectedItems } = useSection75DisputeModalContext()

  const selectedItemsTotal = useSelectedItemsTotal(selectedItems)
  const errorMessage: string = useMemo(() => getDisputeErrorMessage(error, t), [error, t])
  const errorTitle: string = useMemo(() => getDisputeErrorTitle(error, t), [error, t])

  useEffect(() => {
    setIsLoading(isLoading)
    if (isSuccess) {
      setResult({
        success: true,
        error: false,
      })
      return
    }

    if (isError) {
      setResult({
        success: false,
        error: true,
        message: errorMessage,
        title: errorTitle,
      })
    }
  }, [isLoading, errorMessage, errorTitle, isError, isSuccess, setResult, setIsLoading])

  useEffect(() => {
    if (isSuccess) {
      refreshDisputeInfo()
    }
  }, [isSuccess, refreshDisputeInfo])

  return useCallback((): any => {
    const fd = new FormData()

    const firstItemReason = selectedItems[0]?.reason
    const disputeReason =
      reason !== '' ? reason : firstItemReason !== undefined && firstItemReason !== '' ? firstItemReason : DisputeReason.PRODUCT_NOT_RECEIVED
    const amountReasons = selectedItems
      .filter((item) => item.useCustomAmount === true && item.amountReason !== undefined && item.amountReason.trim() !== '')
      .map((item) => `${item.name}: ${item.amountReason?.trim() ?? ''}`)
      .join('\n')

    fd.append('reason', disputeReason)
    fd.append('reasonDetail', disputeReason)
    fd.append('subject', amountReasons)
    fd.append('resolveFailureReason', '')

    const shouldIncludeExpectedDeliveryDate =
      disputeReason === DisputeReason.PRODUCT_NOT_RECEIVED || selectedItems.some((item) => item.reason === DisputeReason.PRODUCT_NOT_RECEIVED)
    if (shouldIncludeExpectedDeliveryDate) {
      fd.append('expectedDeliveryDate', expectedDeliveryDate ?? '')
    }

    const amountToUse = String(selectedItemsTotal.toFixed(2))

    fd.append('amount', amountToUse)

    if (selectedItems.length > 0) {
      // `amount` is the claim for the line; `itemAmount` and `quantity` are what the dispute
      // service multiplies together to cap merchant liability. Sending only `amount` made it
      // fall back to treating the line as a single unit, under-refunding multi-unit lines.
      const transformedItems = selectedItems.map((item) => ({
        name: item.name,
        amount: getSelectedItemDisputeAmount(item),
        itemAmount: item.itemAmount?.amount ?? item.amount.amount,
        quantity: section75ItemQuantity(item.quantity),
        reason: item.reason ?? '',
        description: item.description.trim(),
        fileKeys: item.fileKeys ?? [],
      }))
      fd.append('items', JSON.stringify(transformedItems))
    }

    files.forEach((file: File) => {
      fd.append('files', file, file.name)
    })

    return createS75Dispute({
      orderId: order.id,
      formData: fd,
    })
  }, [reason, order, createS75Dispute, expectedDeliveryDate, files, selectedItems, selectedItemsTotal])
}
